// src/store/builders/useProjectEnqStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import { toast } from "react-hot-toast";
import useAuthStore from "../useAuthStore";

const useProjectEnqStore = create(
  persist(
    (set, get) => ({
      enquiries: [],
      isLoading: false,
      error: null,
      currentFilter: null,

      modalComments: [],
      isCommentsLoading: false,
      isAddingComment: false,

      fetchEnquiries: async (status = null, limit = 10, page = 1) => {
        set({ isLoading: true, error: null, currentFilter: status });

        try {
          const { token, user } = useAuthStore.getState(); // Get the full user object
          if (!token) throw new Error("Authentication token missing!");
          if (!user || !user.id) throw new Error("Builder ID missing!"); // Check for user.id

          const params = new URLSearchParams({
            builderId: user.id, // Use user.id here
            limit,
            page,
          });
          if (status) params.append("status", status);

          const response = await axiosInstancev1.get(
            `/projects/enquiries?${params.toString()}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          set({ enquiries: response.data.enquiries || [] });
        } catch (error) {
          console.error("Fetch Enquiries Error:", error);
          const errorMessage = error.response?.data?.message || "Failed to fetch enquiries";
          set({ error: errorMessage });
          toast.error(errorMessage);
        } finally {
          set({ isLoading: false });
        }
      },

      fetchEnquiryComments: async (enquiryId) => {
        set({ isCommentsLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");

          const response = await axiosInstancev1.get(
            `/projects/enquiry/${enquiryId}/comments`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            set({ modalComments: response.data.comments || [] });
          } else {
            throw new Error(response.data.message || "Failed to fetch comments");
          }
        } catch (error) {
          console.error("Fetch Enquiry Comments Error:", error);
          const errorMessage = error.response?.data?.message || "Failed to fetch comments";
          set({ error: errorMessage, modalComments: [] });
          toast.error(errorMessage);
        } finally {
          set({ isCommentsLoading: false });
        }
      },

      addEnquiryComment: async (enquiryId, commentText) => {
        set({ isAddingComment: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          if (!enquiryId) throw new Error("Enquiry ID is required!");
          if (!commentText.trim()) throw new Error("Comment text cannot be empty.");

          const postCommentResponse = await axiosInstancev1.post(
            `/projects/enquiry/${enquiryId}/comments`,
            { comment: commentText },
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );

          if (!postCommentResponse.data.success) {
            throw new Error(postCommentResponse.data.message || "Failed to add comment to history.");
          }

          const updateEnquiryResponse = await axiosInstancev1.put(
            `/projects/enquiry/${enquiryId}`,
            { comments: commentText },
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );

          if (!updateEnquiryResponse.data.success) {
            console.warn("Comment added to history, but main enquiry update failed:", updateEnquiryResponse.data.message);
            toast.warn("Comment added, but main enquiry update had an issue.");
          } else {
            toast.success("Comment added and enquiry updated successfully!");
          }

          await get().fetchEnquiryComments(enquiryId);
          await get().fetchEnquiries(get().currentFilter);
          return true;
        } catch (error) {
          console.error("Add Enquiry Comment Error:", error);
          const errorMessage = error.response?.data?.message || "Failed to add comment.";
          set({ error: errorMessage });
          toast.error(errorMessage);
          return false;
        } finally {
          set({ isAddingComment: false });
        }
      },

      updatePrimaryEnquiryComment: async (enquiryId, newComment) => {
        // This function's logic seems okay as it doesn't use builderId in the path
        set({ isLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          if (!enquiryId) throw new Error("Enquiry ID is required!");

          const payload = { comments: newComment };

          const response = await axiosInstancev1.put(
            `/projects/enquiry/${enquiryId}`,
            payload,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );

          toast.success("Enquiry comment updated successfully!");
          await get().fetchEnquiries(get().currentFilter);
          return true;
        } catch (error) {
          console.error("Update Primary Comment Error:", error);
          const errorMessage = error.response?.data?.message || "Failed to update comment";
          set({ error: errorMessage });
          toast.error(errorMessage);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      sendProjectInvite: async (customerDucktailId, projectData) => {
        set({ isLoading: true, error: null });
        try {
          const { token, user } = useAuthStore.getState(); // Get the full user object
          if (!token) throw new Error("Authentication token missing!");
          if (!user || !user.id) throw new Error("Builder ID missing for sending invite!"); // Check for user.id

          const enquiry = get().enquiries.find(
            (enq) => enq.customer_ducktail_id === customerDucktailId
          );

          // If no existing enquiry, we might need a different payload or API endpoint
          // For now, assuming an enquiry must exist for the builder to invite
          if (!enquiry) {
            // If you want to allow direct invites without prior enquiry, this logic needs adjustment.
            // For now, throwing an error if no matching enquiry is found.
            toast.error("Customer not found in your enquiries. Cannot send invite directly. Please ask customer to enquire first or ensure the Ducktail ID is correct.");
            set({ isLoading: false });
            return { success: false, message: "Customer not found in enquiries." };
          }

          const payload = {
            builderId: user.id, // Use logged-in builder's ID
            customerId: enquiry.customer_id, // Customer ID from the found enquiry
            status: "pending_approval",
            ...projectData,
          };

          const response = await axiosInstancev1.post("/projects", payload, {
            headers: { Authorization: `Bearer ${token}` },
          });

          return { success: true, message: "Project Invite Sent Successfully" };
        } catch (error) {
          console.error("Project Invite Error:", error);
          const errorMsg = error.response?.data?.message || "Failed to send project invite";
          set({ error: errorMsg });
          toast.error(errorMsg);
          return { success: false, message: errorMsg };
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "project-enquiries-storage",
      getStorage: () => localStorage,
      partialize: (state) => {
        const { modalComments, isCommentsLoading, isAddingComment, ...rest } = state;
        return rest;
      },
    }
  )
);

export default useProjectEnqStore;